// Check if we're on the correct network
import { ethers } from "ethers";

export const verifyNetwork = async () => {
  const chainId = await window.ethereum.request({ method: 'eth_chainId' });
  if (chainId !== '0xa869') {
    console.log('Switching to Fuji network...');
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: '0xa869' }],
      });
    } catch (switchError) {
      // Handle network switching error
      if (switchError.code === 4902) {
        await window.ethereum.request({
          method: 'wallet_addEthereumChain',
          params: [{
            chainId: '0xa869',
            chainName: 'Avalanche Fuji Testnet',
            nativeCurrency: {
              name: 'AVAX',
              symbol: 'AVAX',
              decimals: 18
            },
            rpcUrls: ['https://api.avax-test.network/ext/bc/C/rpc'],
            blockExplorerUrls: ['https://testnet.snowtrace.io/']
          }]
        });
      } else {
        throw switchError;
      }
    }
  }
}

// Get signature
export const getUserData = async (walletAddress, seasonId) => {
  const res = await fetch(`https://cqfjxolodeqrlvaifybm.supabase.co/functions/v1/sign-airdrop`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      walletAddress,
      value: 1, // Adjust this value as needed
      season: seasonId // Add the season ID from your selected season
    })
  });

  if (!res.ok) {
    throw new Error('Failed to get signature');
  }

  const data = await res.json();
  // console.log('Backend response:', data);
  return data.data;
}

// Initialize contract
export const initializeContract = async (signer) => {
  const contract = new ethers.Contract(
    "0xEFE2f671595C3DdbF3D22AE263ee0a76Ec6b7cB1",
    [
      "function claimAirdrop(uint256 amount, uint256 expiry, uint256 season, bytes memory signature) external",
      "function claimed(address account, uint256 season) external view returns (bool)",
      "function token() external view returns (address)",
      "function signer() external view returns (address)"
    ],
    signer
  );

  return contract;
}

// Verify user signature
export const verifyUserSignature = async (userData, contractSigner, messageHash) => {
  // Add the Ethereum signed message prefix as the contract does
  const ethSignedMessageHash = ethers.utils.hashMessage(ethers.utils.arrayify(messageHash));
  // console.log('Ethereum signed message hash:', ethSignedMessageHash);

  // Split and verify the signature
  const sig = ethers.utils.splitSignature(userData.signature);
  // console.log('Signature components:', sig);

  // Verify the signature using the same method as the contract
  const recoveredSigner = ethers.utils.recoverAddress(ethSignedMessageHash, sig);
  // console.log('Recovered signer:', recoveredSigner);
  // console.log('Expected signer:', contractSigner[0]);

  if (recoveredSigner.toLowerCase() !== contractSigner[0].toLowerCase()) {
    return false;
  }

  return true;
}

// Process Claim
export const processClaim = async (walletAddress, userData, seasonId, contract) => {
  try {
    // Get contract signer address for verification
    const contractSigner = await contract.functions.signer();
    // console.log('Contract signer:', contractSigner[0]);

    // Verify the signature matches what the contract expects
    const amount = ethers.BigNumber.from(userData.amount);
    const expiry = ethers.BigNumber.from(userData.expiry);

    // Check expiry first
    const now = Math.floor(Date.now() / 1000);
    const expiryTime = expiry.toNumber();
    // console.log('Current time:', now);
    // console.log('Expiry time:', expiryTime);

    if (now > expiryTime) {
      return {
        success: false,
        message: 'Signature expired'
      }
    }

    if (expiryTime > now + 900) {
      return {
        success: false,
        message: 'Expiry time too far in future'
      }
    }

    // Create message hash exactly as the contract does
    const encodedMessage = ethers.utils.solidityPack(
      ['address', 'uint256', 'uint256', 'uint256'], // Updated parameter types
      [walletAddress, amount, expiry, seasonId] // Added season parameter
    );

    const messageHash = ethers.utils.keccak256(encodedMessage);
    // console.log('Message hash:', messageHash);

    // Verify user signature
    const verified = await verifyUserSignature(userData, contractSigner, messageHash);
    if (!verified) {
      return {
        success: false,
        message: 'Invalid signature - Signer mismatch'
      }
    }

    // Send the transaction with updated parameters
    const tx = await contract.claimAirdrop(
      amount,
      expiry,
      seasonId, // Add season parameter
      userData.signature,
      {
        gasLimit: 300000
      }
    );

    // console.log('Transaction sent:', tx.hash);
    console.log("Waiting for confirmation...");
    const success = await processTransaction(tx, contract);
    if (success) {
      return {
        success: true
      }
    } else {
      return {
        success: false
      }
    }
  } catch (err) {
    // Handle transaction errors
    if (err.message.includes('ECDSAInvalidSignature')) {
      return {
        success: false,
        message: 'Invalid signature - Signer mismatch'
      }
    } else if (err.message.includes('execution reverted')) {
      const reason = err.data?.message || err.message;
      return {
        success: false,
        message: `Transaction reverted: ${reason}`
      }
    } else {
      console.error('Transaction error:', err);
      return {
        success: false,
        message: 'Transaction failed'
      }
    }
  }
}

// Process transaction
export const processTransaction = async (tx, contract) => {
  // Wait for transaction confirmation
  const receipt = await tx.wait();
  // console.log('Transaction receipt:', receipt);

  // Check if transaction was successful
  if (receipt.status === 1) {
    // After successful claim, add token to MetaMask
    const tokenAddress = await contract.token(); // Get token address from contract
    // console.log('Token address:', tokenAddress);

    // Add token to MetaMask
    try {
      await window.ethereum.request({
        method: 'wallet_watchAsset',
        params: {
          type: 'ERC20',
          options: {
            address: tokenAddress,
            symbol: 'NEAL', // Replace with your token symbol
            decimals: 18, // Replace with your token decimals
            image: '' // Optional token logo URL
          },
        },
      });
    } catch (error) {
      console.error('Error adding token to MetaMask:', error);
      // console.log('Error adding token to MetaMask:', error);
    }

    return true;
  } else {
    throw new Error('Transaction failed');
  }
}
